import * as amplitude from "@amplitude/analytics-browser";
import { Button, Flex, Tooltip } from "@chakra-ui/react";
import { Row } from "@tanstack/react-table";
import { FormikContextType, useField, useFormikContext } from "formik";
import { NumberInputControl } from "formik-chakra-ui";
import React from "react";
import { RiDownloadLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { BannerSelection } from "src/components/Table/BannerSelection";
import { RemoteDataTable } from "src/components/Table/RemoteDataTable";
import { rowExpanderColumnDef } from "src/components/Table/rowExpanderColumnDef";
import { ASCENDING, SortType, UNSORTED } from "src/components/Table/SortButton";
import { ColumnDefWithTooltip, TableProps } from "src/components/Table/Table";
import { SearchAndFilterTypes } from "src/constants";
import {
  CapacityConfig,
  exportCapacitiesBySchoolAndEnrollmentPeriod,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { Selection } from "src/hooks/useMultiselectState";
import { checkboxColumnDef } from "src/hooks/useTableSelection";
import { csvExport } from "src/services/dataTransfer";
import * as RD from "src/types/remoteData";
import { Capacity, FormikRow, FormikType } from "./Formik";

export type Mode = "edit" | "view";
type Props = {
  remoteData: RD.RemoteData<
    unknown,
    { capacities: Capacity[]; config: CapacityConfig }
  >;
  configOverrride?: CapacityConfig;
  schoolId: string;
  enrollmentPeriodId: string;
  hideSelection?: boolean;
  mode?: "edit" | "view";
  formikContext?: FormikContextType<FormikType>;
} & Partial<TableProps<Capacity>>;
export const CapacityTable: React.FC<Props> = ({
  remoteData,
  schoolId,
  enrollmentPeriodId,
  hideSelection = false,
  mode = "view",
  formikContext,
  configOverrride,
  ...tableProps
}) => {
  const avelaToast = useAvelaToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortColumn = searchParams.get(
    SearchAndFilterTypes.SortKey
  ) as ColumnId | null;
  const sortType =
    (searchParams.get(SearchAndFilterTypes.SortType) as SortType) || UNSORTED;

  const count = React.useMemo(() => {
    if (!remoteData.hasData()) {
      return 0;
    }
    let count = 0;
    for (const item of remoteData.data.capacities) {
      count +=
        item.programs && item.programs.length > 0 ? item.programs.length : 1;
    }
    return count;
  }, [remoteData]);

  const [selection, onSelectionChange] = React.useState(
    Selection.create((row: Capacity) => row.id)
  );

  const sortedData = React.useMemo(() => {
    if (!remoteData.hasData() || sortType === UNSORTED || !sortColumn) {
      return remoteData.map((data) => data.capacities);
    }

    const sortCapacities = (capacities: Capacity[]): Capacity[] => {
      const sorted = [...capacities];
      sorted.sort((a, b) => {
        let valueA: number;
        let valueB: number;

        if (sortColumn === "totalCapacity") {
          valueA = a.totalCapacity ?? 0;
          valueB = b.totalCapacity ?? 0;
        } else {
          return 0;
        }

        const comparison = valueA - valueB;
        return sortType === ASCENDING ? comparison : -comparison;
      });

      // Also sort programs (sub-rows) for each grade
      return sorted.map((capacity) => {
        if (capacity.programs && capacity.programs.length > 0) {
          return {
            ...capacity,
            programs: sortCapacities(capacity.programs),
          };
        }
        return capacity;
      });
    };

    const sorted = sortCapacities(remoteData.data.capacities);
    return RD.success(sorted);
  }, [remoteData, sortColumn, sortType]);

  const hasSubrows = React.useMemo(
    () =>
      sortedData
        .map((data) =>
          data.some((row) => row.programs && row.programs?.length > 0)
        )
        .withDefault(true),
    [sortedData]
  );

  const handleSortChange = React.useCallback(
    (columnName: string, newSortType: SortType) => {
      if (newSortType === UNSORTED) {
        searchParams.delete(SearchAndFilterTypes.SortKey);
        searchParams.delete(SearchAndFilterTypes.SortType);
      } else {
        searchParams.set(SearchAndFilterTypes.SortKey, columnName);
        searchParams.set(SearchAndFilterTypes.SortType, newSortType);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const sortableColumnIds = React.useMemo(() => {
    return mode === "view" ? ["totalCapacity"] : [];
  }, [mode]);

  const columns = React.useMemo(() => {
    const cols: ColumnDefWithTooltip<Capacity>[] = [
      ...(hideSelection || mode === "edit"
        ? []
        : [
            checkboxColumnDef({
              id: "Selection",
              selection,
              onSelectionChange,
              count,
            }),
          ]),
      hasSubrows
        ? rowExpanderColumnDef({
            header: "Grade",
            size: 500,
            cell: ({ row }) => {
              return (
                <Flex fontStyle={row.depth > 0 ? "italic" : undefined}>
                  {row.original.name}
                </Flex>
              );
            },
          })
        : {
            header: "Grade",
            size: 500,
            cell: ({ row }) => {
              return (
                <Flex fontStyle={row.depth > 0 ? "italic" : undefined}>
                  {row.original.name}
                </Flex>
              );
            },
          },
      {
        id: "totalCapacity",
        header: "Total capacity",
        cell: ({ row }) => {
          if (mode === "edit") {
            return <EditCell row={row} field="totalCapacity" />;
          }
          return (
            <Flex
              justifyContent="center"
              fontStyle={row.depth > 0 ? "italic" : undefined}
            >
              {row.original.totalCapacity}
            </Flex>
          );
        },
      },
      {
        header: "Offered",
        cell: ({ row }) => (
          <Flex
            justifyContent="center"
            fontStyle={row.depth > 0 ? "italic" : undefined}
          >
            {row.original.offered}
          </Flex>
        ),
      },
      {
        header: "Accepted",
        cell: ({ row }) => (
          <Flex
            justifyContent="center"
            fontStyle={row.depth > 0 ? "italic" : undefined}
          >
            {row.original.accepted}
          </Flex>
        ),
      },
    ];
    if (remoteData.hasData()) {
      const config = configOverrride ?? remoteData.data.config;
      if (config.additionalSeatsTaken1.enabled) {
        cols.push({
          id: "additionalSeatsTaken1",
          header: config.additionalSeatsTaken1.label,
          cell: ({ row }) => {
            if (mode === "edit") {
              return <EditCell row={row} field="additionalSeatsTaken1" />;
            }
            return (
              <Flex
                justifyContent="center"
                fontStyle={row.depth > 0 ? "italic" : undefined}
              >
                {row.original.additionalSeatsTaken1}
              </Flex>
            );
          },
        });
      }
      if (config.additionalSeatsTaken2.enabled) {
        cols.push({
          id: "additionalSeatsTaken2",
          header: config.additionalSeatsTaken2.label,
          cell: ({ row }) => {
            if (mode === "edit") {
              return <EditCell row={row} field="additionalSeatsTaken2" />;
            }
            return (
              <Flex
                justifyContent="center"
                fontStyle={row.depth > 0 ? "italic" : undefined}
              >
                {row.original.additionalSeatsTaken2}
              </Flex>
            );
          },
        });
      }
    }

    cols.push({
      id: "seatsAvailable",
      header: "Available seats",
      cell: ({ row }) => {
        if (mode === "edit") {
          return <AvailableSeatsCell row={row} remoteData={remoteData} />;
        }

        return (
          <Flex
            justifyContent="center"
            fontStyle={row.depth > 0 ? "italic" : undefined}
          >
            {configOverrride
              ? previewSeatsAvailable(configOverrride, row.original)
              : row.original.seatsAvailable}
          </Flex>
        );
      },
    });

    cols.push({
      header: "Waitlisted",
      cell: ({ row }) => {
        // Only show waitlisted count at grade level (depth 0), not at program level (depth > 0)
        if (row.depth > 0) {
          return <Flex justifyContent="center" fontStyle="italic"></Flex>;
        }
        return (
          <Flex justifyContent="center">{row.original.waitlisted ?? 0}</Flex>
        );
      },
    });

    return cols;
  }, [
    hideSelection,
    mode,
    selection,
    count,
    hasSubrows,
    remoteData,
    configOverrride,
  ]);

  return (
    <>
      <RemoteDataTable<Capacity>
        {...tableProps}
        count={count}
        hideItemCount={true}
        expandAllProps={{
          expandAllSubRows: true,
          onToggle: (toggle) => {
            if (toggle === "expand") {
              amplitude.track("Clicked expand all");
            } else {
              amplitude.track("Clicked collapse all");
            }
          },
        }}
        tablePadding="0"
        data={sortedData}
        getSubRows={hasSubrows ? (row) => row.programs : undefined}
        sortableColumnIds={sortableColumnIds}
        onChangeSort={handleSortChange}
        getCellStyleProps={(cell) => {
          const header = cell.column.columnDef.header as string;
          if (header === "Waitlisted") {
            return {
              borderLeftWidth: "1px",
              borderLeftStyle: "solid",
            };
          }
          return header &&
            [
              "Available seats",
              ...(hideSelection ? [] : ["Grade"]),
              "Total capacity",
              "Offered",
            ].includes(header)
            ? {
                borderLeftWidth: "1px",
                borderLeftStyle: "solid",
              }
            : {};
        }}
        getHeaderStyleProps={(cell) => {
          const header = cell.column.columnDef.header as string;
          if (header === "Waitlisted") {
            return {
              borderLeftWidth: "1px",
              borderLeftStyle: "solid",
              textAlign: "center",
            };
          }
          return {
            ...(header &&
            [
              "Available seats",
              ...(hideSelection ? [] : ["Grade"]),
              "Total capacity",
              "Offered",
            ].includes(header)
              ? {
                  borderLeftWidth: "1px",
                  borderLeftStyle: "solid",
                }
              : {}),
            ...(["Grade"].includes(header)
              ? { textAlign: "left", paddingLeft: hasSubrows ? "10" : "4" }
              : { textAlign: "center" }),
          };
        }}
        columns={columns}
        banner={(data) => (
          <BannerSelection selection={selection}>
            <Button
              size="sm"
              leftIcon={<RiDownloadLine />}
              variant="ghost"
              onClick={async () => {
                amplitude.track("Clicked contextual export");
                const loadingToastId = avelaToast({
                  title: `Export in progress`,
                  description: `Download will start shortly`,
                  status: "loading",
                });
                try {
                  const ids = await selection.materializeKeys(async () =>
                    data.flatMap((grade) =>
                      grade.programs && grade.programs.length > 0
                        ? grade.programs.map((program) => program.id)
                        : grade.id
                    )
                  );

                  const records =
                    await exportCapacitiesBySchoolAndEnrollmentPeriod({
                      path: {
                        schoolId,
                        enrollmentPeriodId,
                      },
                      body: {
                        ids,
                      },
                    });
                  if (records.data?.data) {
                    csvExport(records.data?.data, "capacities.csv");
                  } else {
                    console.error("Empty export data");
                    avelaToast.genericError();
                  }
                } catch (error) {
                  console.error(error);
                  avelaToast.genericError();
                } finally {
                  avelaToast.close(loadingToastId);
                }
              }}
            >
              Export
            </Button>
          </BannerSelection>
        )}
      />
    </>
  );
};

type NumberInputProps = {
  name: string;
  ariaLabel: string;
};

const NumberInput: React.FC<NumberInputProps> = ({ name, ariaLabel }) => {
  const [, meta] = useField<string>(name);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const input = wrapperRef.current?.querySelector("input");
    if (input) {
      input.setAttribute("data-1p-ignore", "");
    }
  }, []);

  return (
    <Tooltip label={meta.error} bg="white" color="red.500" hasArrow>
      <div ref={wrapperRef}>
        <NumberInputControl
          justifySelf="flex-start"
          aria-label={ariaLabel}
          name={name}
          errorMessageProps={{ display: "none" }}
          numberInputProps={{
            precision: 0,
            sx: {
              input: {
                textAlign: "center",
                paddingRight: 4,
                bg: "white",
              },
            },
          }}
          showStepper={false}
        />
      </div>
    </Tooltip>
  );
};

const AvailableSeatsCell: React.FC<{
  row: Row<Capacity>;
  remoteData: RD.RemoteData<
    unknown,
    { capacities: Capacity[]; config: CapacityConfig }
  >;
}> = ({ row, remoteData }) => {
  const children = row.originalSubRows ?? [];

  let seatsAvailable = row.original.seatsAvailable;

  const formik = useFormikContext<FormikType>();

  if (remoteData.hasData()) {
    const config = remoteData.data.config;
    if (children.length === 0) {
      seatsAvailable = calculateSeatsAvailable(
        config,
        row.original,
        formik.values[row.original.id]
      );
    } else {
      // sum of children's available seats
      seatsAvailable = 0;
      for (const child of children) {
        seatsAvailable += calculateSeatsAvailable(
          config,
          child,
          formik.values[child.id]
        );
      }
    }
  }

  return (
    <Flex
      justifyContent="center"
      fontStyle={row.depth > 0 ? "italic" : undefined}
    >
      {isNaN(seatsAvailable) ? "-" : seatsAvailable}
    </Flex>
  );
};

const EditCell: React.FC<{
  row: Row<Capacity>;
  field: "totalCapacity" | "additionalSeatsTaken1" | "additionalSeatsTaken2";
}> = ({ row, field }) => {
  const children = row.originalSubRows ?? [];
  const formik = useFormikContext<FormikType>();

  if (children.length === 0) {
    return (
      <NumberInput name={`${row.original.id}.${field}`} ariaLabel={field} />
    );
  }

  let total = row.original[field];

  // sum of children's value
  total = 0;
  for (const child of children) {
    total += parseInt(formik.values[child.id]?.[field] ?? "");
  }

  return (
    <Flex
      justifyContent="center"
      fontStyle={row.depth > 0 ? "italic" : undefined}
    >
      {isNaN(total) ? "-" : total}
    </Flex>
  );
};

function calculateSeatsAvailable(
  config: CapacityConfig,
  original: Capacity,
  rowValue: FormikRow | undefined
): number {
  let seatsAvailable =
    parseInt(rowValue?.totalCapacity ?? "") -
    original.offered -
    original.accepted;

  if (config.additionalSeatsTaken1.enabled) {
    seatsAvailable -= parseInt(rowValue?.additionalSeatsTaken1 ?? "");
  }
  if (config.additionalSeatsTaken2.enabled) {
    seatsAvailable -= parseInt(rowValue?.additionalSeatsTaken2 ?? "");
  }

  return seatsAvailable;
}

type ColumnId = "totalCapacity";

function previewSeatsAvailable(
  configOverrride: CapacityConfig,
  capacity: Capacity
): string {
  let seatsAvailable =
    capacity.totalCapacity - capacity.offered - capacity.accepted;
  if (configOverrride.additionalSeatsTaken1.enabled) {
    seatsAvailable -= capacity.additionalSeatsTaken1 ?? 0;
  }
  if (configOverrride.additionalSeatsTaken2.enabled) {
    seatsAvailable -= capacity.additionalSeatsTaken2 ?? 0;
  }
  const delta = seatsAvailable - capacity.seatsAvailable;
  if (delta > 0) {
    return `${seatsAvailable} (+${delta})`;
  }
  if (delta < 0) {
    return `${seatsAvailable} (${delta})`;
  }

  return `${seatsAvailable}`;
}

export const TableLoading = () => {
  return (
    <CapacityTable
      remoteData={RD.loading()}
      schoolId=""
      enrollmentPeriodId=""
    />
  );
};
